<template>
  <main class="privacy-policy-page">
    <div class="privacy-policy-page__inner">
      <h1>{{ policyTitle }}</h1>

      <article v-if="isEnglish" class="privacy-policy-page__content privacy-policy-page__content--en" v-html="englishPolicyHtml"></article>
      <article v-else class="privacy-policy-page__content" @click="handleArticleClick">
        <template v-for="(block, index) in policyBlocks" :key="`${block.type}-${index}`">
          <h2 v-if="block.type === 'heading'" v-html="formatText(block.text)"></h2>
          <h3 v-else-if="block.type === 'subheading'" v-html="formatText(block.text)"></h3>
          <div v-else-if="block.type === 'list'" class="privacy-policy-page__block">
            <p v-for="(line, lineIndex) in block.intro" :key="`intro-${lineIndex}`" v-html="formatText(line)"></p>
            <ul>
              <li v-for="(item, itemIndex) in block.items" :key="`item-${itemIndex}`" v-html="formatText(item)"></li>
            </ul>
            <p v-for="(line, lineIndex) in block.tail" :key="`tail-${lineIndex}`" v-html="formatText(line)"></p>
          </div>
          <div v-else class="privacy-policy-page__block">
            <p v-for="(line, lineIndex) in block.lines" :key="lineIndex" v-html="formatText(line)"></p>
          </div>
        </template>
      </article>
    </div>
  </main>
</template>

<script>
const { PRIVACY_POLICY_EN } = require('@/data/privacy-policy-en.cjs')
const { PRIVACY_POLICY_ZH } = require('@/data/privacy-policy-zh.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')

const MAIN_HEADING_PATTERN = /^(?:一|二|三|四|五|六|七|八|九|十|十一|十二|十三|十四|十五|十六|十七)、/
const SUBHEADING_PATTERN = /^\d+\.\s/

function parsePolicyBlock(text) {
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean)
  if (text === '隐私政策' || MAIN_HEADING_PATTERN.test(text)) return { type: 'heading', text }
  if (SUBHEADING_PATTERN.test(text)) return { type: 'subheading', text }

  const firstListItem = lines.findIndex(line => line.endsWith('；'))
  if (firstListItem < 0) return { type: 'paragraphs', lines }

  let listEnd = firstListItem
  while (listEnd + 1 < lines.length) {
    listEnd += 1
    if (!lines[listEnd].endsWith('；')) break
  }
  return {
    type: 'list',
    intro: lines.slice(0, firstListItem),
    items: lines.slice(firstListItem, listEnd + 1),
    tail: lines.slice(listEnd + 1)
  }
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export default {
  name: 'PrivacyPolicy',
  mixins: [siteLanguageMixin],
  computed: {
    isEnglish() {
      return this.currentLanguage === 'EN'
    },
    policyTitle() {
      return this.isEnglish ? PRIVACY_POLICY_EN.title : '源诺医疗（中国）官网个人信息保护政策'
    },
    englishPolicyHtml() {
      return PRIVACY_POLICY_EN.html
    },
    policyBlocks() {
      return PRIVACY_POLICY_ZH.split(/\n{2,}/).map(block => parsePolicyBlock(block.trim())).filter(block => block.text || block.lines?.length || block.items?.length)
    }
  },
  created() {
    this.updateDocumentTitle()
  },
  watch: {
    currentLanguage() {
      this.updateDocumentTitle()
    }
  },
  methods: {
    updateDocumentTitle() {
      document.title = this.policyTitle
    },
    formatText(value) {
      return escapeHtml(value)
        .replace(/(info@anovator\.com|emma@anovator\.com)/g, '<a href="mailto:$1">$1</a>')
        .replace(/隐私设置 \/ Privacy Preferences/g, '<button type="button" class="privacy-policy-page__settings" data-privacy-settings>隐私设置 / Privacy Preferences</button>')
    },
    handleArticleClick(event) {
      if (!event.target.closest('[data-privacy-settings]')) return
      window.dispatchEvent(new CustomEvent('anovator:open-cookie-settings'))
    }
  }
}
</script>

<style scoped>
.privacy-policy-page,
.privacy-policy-page * {
  box-sizing: border-box;
}

.privacy-policy-page {
  min-height: calc(100vh - 177.5px);
  padding: calc(var(--global-header-height, 64px) + 30px) 0 120px;
  color: rgba(0, 0, 0, 0.8);
  background: #fff;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
}

.privacy-policy-page__inner {
  width: min(1296px, calc(100vw - 144px));
  margin: 0 auto;
}

.privacy-policy-page h1 {
  margin: 0;
  color: #000;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 50px;
  text-align: center;
}

.privacy-policy-page__content {
  margin-top: 58px;
  font-size: 14px;
  line-height: 1.55;
}

.privacy-policy-page__content h2 {
  margin: 34px 0 14px;
  color: #000;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
}

.privacy-policy-page__content h2:first-child { margin-top: 0; }

.privacy-policy-page__content h3 {
  margin: 22px 0 10px;
  color: rgba(0, 0, 0, 0.8);
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
}

.privacy-policy-page__block { margin: 0 0 14px; }
.privacy-policy-page__block p { margin: 0 0 7px; color: rgba(0, 0, 0, 0.8); }
.privacy-policy-page__block ul { margin: 5px 0 10px; padding-left: 24px; }
.privacy-policy-page__block li { margin: 0 0 4px; }
.privacy-policy-page__content :deep(a) { color: inherit; text-decoration: underline; }

.privacy-policy-page__content--en :deep(.last-updated) { margin: 0 0 28px; }
.privacy-policy-page__content--en :deep(h2) {
  margin: 34px 0 14px;
  color: #000;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
}
.privacy-policy-page__content--en :deep(p) { margin: 0 0 14px; color: rgba(0, 0, 0, 0.8); font-size: 14px; line-height: 1.55; }
.privacy-policy-page__content--en :deep(ul) { margin: 5px 0 14px; padding-left: 24px; }
.privacy-policy-page__content--en :deep(li) { margin: 0 0 4px; font-size: 14px; line-height: 1.55; }
.privacy-policy-page__content--en :deep(address) { margin: 0 0 14px; font-size: 14px; font-style: normal; line-height: 1.55; }

.privacy-policy-page__content :deep(.privacy-policy-page__settings) {
  display: inline;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.privacy-policy-page__content :deep(a:focus-visible),
.privacy-policy-page__content :deep(.privacy-policy-page__settings:focus-visible) {
  outline: 2px solid #ef0a6a;
  outline-offset: 2px;
}

@media (max-width: 700px) {
  .privacy-policy-page {
    min-height: calc(100vh - 188px);
    padding: calc(var(--global-header-height, 50px) + 24px) 0 72px;
  }

  .privacy-policy-page__inner { width: calc(100vw - 32px); }
  .privacy-policy-page h1 { font-size: 28px; line-height: 39px; }
  .privacy-policy-page__content { margin-top: 40px; }
  .privacy-policy-page__content h2 { margin-top: 30px; }
  .privacy-policy-page__content--en :deep(h2) { margin-top: 30px; }
  .privacy-policy-page .privacy-policy-page__content--en :deep(p),
  .privacy-policy-page .privacy-policy-page__content--en :deep(li),
  .privacy-policy-page .privacy-policy-page__content--en :deep(address) { font-size: 14px !important; }
}
</style>
